import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import TabNavigation from '../../components/TabNavigation';
import { useSite } from '../../context/SiteContext';
import { useAlert } from '../../context/AlertContext';
import {
  Zap, Flame, Droplets, Trash2, TrendingDown, DollarSign,
  Download, ArrowLeft, BarChart3, Leaf, Building2, Calendar,
  ChevronDown, RefreshCw
} from 'lucide-react';

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);

const CorporateERSView = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { isCorpUser, availableSites, activeSiteId } = useSite();
  const { showError } = useAlert();

  // Filters
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [selectedSites, setSelectedSites] = useState([]);
  const [showSiteFilter, setShowSiteFilter] = useState(false);

  // Data states
  const [consumption, setConsumption] = useState(null);
  const [intensity, setIntensity] = useState(null);
  const [benchmarking, setBenchmarking] = useState(null);
  const [savings, setSavings] = useState(null);
  const [costBySite, setCostBySite] = useState(null);
  const [decarbonisation, setDecarbonisation] = useState(null);
  const [loading, setLoading] = useState(true);

  const siteParams = useCallback(() => {
    if (selectedSites.length > 0) {
      return selectedSites.map(id => `sites[]=${id}`).join('&');
    }
    return '';
  }, [selectedSites]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    const base = `${API}/ers/corporate`;
    const sp = siteParams();
    const yearParam = `year=${selectedYear}`;
    const qs = sp ? `${yearParam}&${sp}` : yearParam;

    const requests = [
      axios.get(`${base}/consumption?${qs}`, { headers }).catch(() => ({ data: null })),
      axios.get(`${base}/intensity?${qs}&normalize_by=production`, { headers }).catch(() => ({ data: null })),
      axios.get(`${base}/benchmarking?${qs}`, { headers }).catch(() => ({ data: null })),
      axios.get(`${base}/savings?${qs}`, { headers }).catch(() => ({ data: null })),
      axios.get(`${base}/cost-by-site?${qs}`, { headers }).catch(() => ({ data: null })),
      axios.get(`${base}/decarbonisation?${qs}`, { headers }).catch(() => ({ data: null })),
    ];

    try {
      const [consRes, intRes, benchRes, savRes, costRes, decarbRes] = await Promise.all(requests);
      setConsumption(consRes.data);
      setIntensity(intRes.data);
      setBenchmarking(benchRes.data);
      setSavings(savRes.data);
      setCostBySite(costRes.data);
      setDecarbonisation(decarbRes.data);
    } catch (error) {
      console.error('Failed to fetch corporate ERS data:', error);
      showError('Failed to load corporate energy data');
    } finally {
      setLoading(false);
    }
  }, [selectedYear, siteParams, showError]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const toggleSiteSelection = (siteId) => {
    setSelectedSites(prev =>
      prev.includes(siteId) ? prev.filter(id => id !== siteId) : [...prev, siteId]
    );
  };

  const handleExport = async (format) => {
    try {
      const token = localStorage.getItem('token');
      const sp = siteParams();
      const qs = sp ? `year=${selectedYear}&${sp}&format=${format}` : `year=${selectedYear}&format=${format}`;
      const response = await axios.get(`${API}/ers/corporate/export?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `corporate_ers_report_${selectedYear}.${format === 'pdf' ? 'pdf' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      showError('Export not yet available');
    }
  };

  const formatNumber = (num, decimals = 0) => {
    if (num == null || isNaN(num)) return '—';
    return Number(num).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <button
                  onClick={() => navigate('/energy-resources')}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <h1 className="text-3xl font-bold text-gray-900">Corporate Energy Overview</h1>
              </div>
              <p className="text-gray-600 ml-11">Cross-site energy & resource aggregation</p>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-3 flex-wrap">
              {/* Year selector */}
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  {YEAR_OPTIONS.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              {/* Site filter */}
              <div className="relative">
                <button
                  onClick={() => setShowSiteFilter(!showSiteFilter)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm flex items-center gap-2 hover:bg-gray-50"
                >
                  <Building2 className="w-4 h-4" />
                  {selectedSites.length === 0 ? 'All Sites' : `${selectedSites.length} site(s)`}
                  <ChevronDown className="w-3 h-3" />
                </button>
                {showSiteFilter && (
                  <div className="absolute right-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-64 overflow-y-auto">
                    <div className="p-2 border-b border-gray-100">
                      <button
                        onClick={() => setSelectedSites([])}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Clear selection (All Sites)
                      </button>
                    </div>
                    {availableSites.map(site => (
                      <label
                        key={site.site_id}
                        className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedSites.includes(site.site_id)}
                          onChange={() => toggleSiteSelection(site.site_id)}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-700">{site.site_name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Refresh */}
              <button
                onClick={fetchAllData}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Refresh data"
              >
                <RefreshCw className="w-4 h-4" />
              </button>

              {/* Export */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleExport('excel')}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-l-lg hover:bg-gray-50 flex items-center gap-1"
                >
                  <Download className="w-3 h-3" /> Excel
                </button>
                <button
                  onClick={() => handleExport('pdf')}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-r-lg hover:bg-gray-50 border-l-0 flex items-center gap-1"
                >
                  <Download className="w-3 h-3" /> PDF
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Total Consumption Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <ConsumptionCard
            icon={<Zap className="w-6 h-6 text-yellow-600" />}
            label="Electricity"
            value={formatNumber(consumption?.total_electricity_kwh)}
            unit="kWh"
            bgClass="from-yellow-50 to-yellow-100 border-yellow-200"
            textClass="text-yellow-900"
          />
          <ConsumptionCard
            icon={<Flame className="w-6 h-6 text-orange-600" />}
            label="Fuel"
            value={formatNumber(consumption?.total_fuel_kwh)}
            unit="kWh (eq)"
            bgClass="from-orange-50 to-orange-100 border-orange-200"
            textClass="text-orange-900"
          />
          <ConsumptionCard
            icon={<Droplets className="w-6 h-6 text-cyan-600" />}
            label="Water"
            value={formatNumber(consumption?.total_water_kl)}
            unit="kL"
            bgClass="from-cyan-50 to-cyan-100 border-cyan-200"
            textClass="text-cyan-900"
          />
          <ConsumptionCard
            icon={<Trash2 className="w-6 h-6 text-gray-600" />}
            label="Waste"
            value={formatNumber(consumption?.total_waste_mt, 1)}
            unit="MT"
            bgClass="from-gray-50 to-gray-100 border-gray-200"
            textClass="text-gray-900"
          />
        </div>

        {/* Energy Intensity */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-indigo-600" />
            Energy Intensity (Normalized by Production)
          </h2>
          {intensity && intensity.sites ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-3 font-semibold text-gray-700">Site</th>
                    <th className="px-4 py-3 font-semibold text-gray-700 text-right">Production (MT)</th>
                    <th className="px-4 py-3 font-semibold text-gray-700 text-right">Electricity (kWh/MT)</th>
                    <th className="px-4 py-3 font-semibold text-gray-700 text-right">Water (kL/MT)</th>
                    <th className="px-4 py-3 font-semibold text-gray-700 text-right">Total Energy (kWh/MT)</th>
                  </tr>
                </thead>
                <tbody>
                  {intensity.sites.map((site, idx) => (
                    <tr key={site.site_id || idx} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{site.site_name}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatNumber(site.production)}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatNumber(site.electricity_intensity, 2)}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatNumber(site.water_intensity, 2)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-indigo-700">{formatNumber(site.total_energy_intensity, 2)}</td>
                    </tr>
                  ))}
                </tbody>
                {intensity.totals && (
                  <tfoot>
                    <tr className="border-t-2 border-gray-300 bg-indigo-50 font-semibold">
                      <td className="px-4 py-3 text-gray-900">Overall</td>
                      <td className="px-4 py-3 text-right text-gray-900">{formatNumber(intensity.totals.production)}</td>
                      <td className="px-4 py-3 text-right text-gray-900">{formatNumber(intensity.totals.electricity_intensity, 2)}</td>
                      <td className="px-4 py-3 text-right text-gray-900">{formatNumber(intensity.totals.water_intensity, 2)}</td>
                      <td className="px-4 py-3 text-right text-indigo-700">{formatNumber(intensity.totals.total_energy_intensity, 2)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          ) : (
            <EmptyState message="No intensity data available for the selected period." />
          )}
        </div>

        {/* Site Benchmarking Table */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-green-600" />
            Site Benchmarking
          </h2>
          {benchmarking && benchmarking.sites && benchmarking.sites.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-3 font-semibold text-gray-700">Site</th>
                    <th className="px-4 py-3 font-semibold text-gray-700">Group</th>
                    <th className="px-4 py-3 font-semibold text-gray-700 text-right">Electricity (kWh)</th>
                    <th className="px-4 py-3 font-semibold text-gray-700 text-right">Fuel (kWh eq)</th>
                    <th className="px-4 py-3 font-semibold text-gray-700 text-right">Water (kL)</th>
                    <th className="px-4 py-3 font-semibold text-gray-700 text-right">Waste (MT)</th>
                    <th className="px-4 py-3 font-semibold text-gray-700 text-right">Cost</th>
                    <th className="px-4 py-3 font-semibold text-gray-700 text-right">Intensity</th>
                  </tr>
                </thead>
                <tbody>
                  {benchmarking.sites.map((site, idx) => (
                    <tr key={site.site_id || idx} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{site.site_name}</td>
                      <td className="px-4 py-3 text-gray-600">{site.group_label || '—'}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatNumber(site.electricity_kwh)}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatNumber(site.fuel_kwh)}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatNumber(site.water_kl)}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatNumber(site.waste_mt, 1)}</td>
                      <td className="px-4 py-3 text-right text-gray-700">
                        {site.cost != null ? `${site.currency || ''} ${formatNumber(site.cost)}` : '—'}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-green-700">
                        {formatNumber(site.energy_intensity, 2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState message="No benchmarking data available for the selected period." />
          )}
        </div>

        {/* Savings Tracker */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-600" />
            Savings Tracker
          </h2>
          {savings && savings.sites && savings.sites.length > 0 ? (
            <div className="space-y-3">
              {savings.sites.map((site, idx) => {
                const maxSaving = Math.max(...savings.sites.map(s => s.total_savings || 0), 1);
                const pct = ((site.total_savings || 0) / maxSaving) * 100;
                return (
                  <div key={site.site_id || idx}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">{site.site_name}</span>
                      <span className="text-sm font-bold text-emerald-700">
                        {site.currency || ''} {formatNumber(site.total_savings)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-gradient-to-r from-emerald-400 to-emerald-600 h-3 rounded-full transition-all"
                        style={{ width: `${Math.max(pct, 2)}%` }}
                      />
                    </div>
                    {site.savings_breakdown && (
                      <div className="flex gap-4 mt-1 text-xs text-gray-500">
                        {site.savings_breakdown.electricity != null && (
                          <span>Electricity: {formatNumber(site.savings_breakdown.electricity)}</span>
                        )}
                        {site.savings_breakdown.fuel != null && (
                          <span>Fuel: {formatNumber(site.savings_breakdown.fuel)}</span>
                        )}
                        {site.savings_breakdown.water != null && (
                          <span>Water: {formatNumber(site.savings_breakdown.water)}</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {savings.total != null && (
                <div className="pt-3 border-t border-gray-200 flex items-center justify-between">
                  <span className="text-sm font-bold text-gray-900">Total Savings</span>
                  <span className="text-lg font-bold text-emerald-700">
                    {savings.currency || ''} {formatNumber(savings.total)}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <EmptyState message="No savings data available for the selected period." />
          )}
        </div>

        {/* Cost by Site */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-blue-600" />
            Cost Breakdown by Site
          </h2>
          {costBySite && costBySite.sites && costBySite.sites.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-3 font-semibold text-gray-700">Site</th>
                    <th className="px-4 py-3 font-semibold text-gray-700 text-right">Electricity Cost</th>
                    <th className="px-4 py-3 font-semibold text-gray-700 text-right">Fuel Cost</th>
                    <th className="px-4 py-3 font-semibold text-gray-700 text-right">Water Cost</th>
                    <th className="px-4 py-3 font-semibold text-gray-700 text-right">Total Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {costBySite.sites.map((site, idx) => (
                    <tr key={site.site_id || idx} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{site.site_name}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatNumber(site.electricity_cost)}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatNumber(site.fuel_cost)}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatNumber(site.water_cost)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-blue-700">{formatNumber(site.total_cost)}</td>
                    </tr>
                  ))}
                </tbody>
                {costBySite.total != null && (
                  <tfoot>
                    <tr className="border-t-2 border-gray-300 bg-blue-50 font-semibold">
                      <td className="px-4 py-3 text-gray-900">Total ({costBySite.currency || ''})</td>
                      <td className="px-4 py-3 text-right text-gray-900">{formatNumber(costBySite.total_electricity)}</td>
                      <td className="px-4 py-3 text-right text-gray-900">{formatNumber(costBySite.total_fuel)}</td>
                      <td className="px-4 py-3 text-right text-gray-900">{formatNumber(costBySite.total_water)}</td>
                      <td className="px-4 py-3 text-right text-blue-700">{formatNumber(costBySite.total)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          ) : (
            <EmptyState message="No cost data available for the selected period." />
          )}
        </div>

        {/* Decarbonisation Progress */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Leaf className="w-5 h-5 text-green-600" />
            Decarbonisation Progress
          </h2>
          {decarbonisation && decarbonisation.sites && decarbonisation.sites.length > 0 ? (
            <div className="space-y-4">
              {decarbonisation.sites.map((site, idx) => {
                const reduction = site.reduction_pct || 0;
                const isPositive = reduction >= 0;
                return (
                  <div key={site.site_id || idx}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">{site.site_name}</span>
                      <div className="flex items-center gap-2">
                        <TrendingDown className={`w-4 h-4 ${isPositive ? 'text-green-600' : 'text-red-500 rotate-180'}`} />
                        <span className={`text-sm font-bold ${isPositive ? 'text-green-700' : 'text-red-600'}`}>
                          {isPositive ? '-' : '+'}{Math.abs(reduction).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full transition-all ${isPositive ? 'bg-gradient-to-r from-green-400 to-green-600' : 'bg-gradient-to-r from-red-300 to-red-500'}`}
                        style={{ width: `${Math.min(Math.abs(reduction), 100)}%` }}
                      />
                    </div>
                    <div className="flex gap-4 mt-1 text-xs text-gray-500">
                      {site.baseline_emissions != null && (
                        <span>Baseline: {formatNumber(site.baseline_emissions)} tCO2e</span>
                      )}
                      {site.current_emissions != null && (
                        <span>Current: {formatNumber(site.current_emissions)} tCO2e</span>
                      )}
                    </div>
                  </div>
                );
              })}
              {decarbonisation.overall_reduction_pct != null && (
                <div className="pt-3 border-t border-gray-200 flex items-center justify-between">
                  <span className="text-sm font-bold text-gray-900">Overall Reduction</span>
                  <span className={`text-lg font-bold ${decarbonisation.overall_reduction_pct >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                    {decarbonisation.overall_reduction_pct >= 0 ? '-' : '+'}
                    {Math.abs(decarbonisation.overall_reduction_pct).toFixed(1)}%
                  </span>
                </div>
              )}
            </div>
          ) : (
            <EmptyState message="No decarbonisation data available. Baseline data is required." />
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Helper Components ── */

const ConsumptionCard = ({ icon, label, value, unit, bgClass, textClass }) => (
  <div className={`bg-gradient-to-br ${bgClass} border rounded-xl p-6`}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm font-semibold text-gray-700">{label}</span>
      {icon}
    </div>
    <div className={`text-2xl font-bold ${textClass}`}>
      {value} <span className="text-sm font-normal text-gray-600">{unit}</span>
    </div>
  </div>
);

const EmptyState = ({ message }) => (
  <div className="text-center py-8 text-gray-500">
    <BarChart3 className="w-10 h-10 mx-auto mb-2 text-gray-300" />
    <p className="text-sm">{message}</p>
  </div>
);

export default CorporateERSView;
